import { ReactNode, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface WorkspaceTab<T extends string> {
  id: T;
  label: string;
  /** Something in this tab waiting on the reader. Shown as a small count; omitted when zero. */
  count?: number;
}

/**
 * Which tab of a one-person workspace is open, kept in the URL as `?tab=`.
 *
 * In the URL so a link can land on a tab (a flag on the dashboard opens Sessions, not the top of the
 * page) and so Back steps between tabs the way it steps between pages. The first tab is the default
 * and leaves the URL clean.
 *
 * A tab once opened stays mounted, hidden, rather than being torn down: a plan half-built in Plans, or a
 * note half-typed in Notes, must survive a look at another tab.
 */
export function useWorkspaceTab<T extends string>(ids: readonly T[]): {
  active: T;
  select: (id: T) => void;
  isMounted: (id: T) => boolean;
} {
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const active = ids.find((id) => id === requested) ?? ids[0];

  const [visited, setVisited] = useState<ReadonlySet<T>>(() => new Set([active]));
  useEffect(() => {
    setVisited((previous) => (previous.has(active) ? previous : new Set(previous).add(active)));
  }, [active]);

  function select(id: T) {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (id === ids[0]) next.delete("tab");
      else next.set("tab", id);
      return next;
    });
  }

  return { active, select, isMounted: (id: T) => id === active || visited.has(id) };
}

/** The tab row: underlined across the top on desktop, a sticky row of pills on a phone (index.css). */
export function WorkspaceTabs<T extends string>({
  tabs,
  active,
  onSelect,
  label,
}: {
  tabs: WorkspaceTab<T>[];
  active: T;
  onSelect: (id: T) => void;
  label: string;
}) {
  return (
    <div className="workspace-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={`tab-${tab.id}`}
          aria-selected={tab.id === active}
          aria-controls={`panel-${tab.id}`}
          className={tab.id === active ? "workspace-tab active" : "workspace-tab"}
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
          {tab.count ? (
            <span className="workspace-tab-count" aria-label={`${tab.count} waiting`}>
              {tab.count}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

/** One tab's content. Rendered once it has been opened, then hidden rather than unmounted. */
export function WorkspacePanel<T extends string>({
  id,
  active,
  mounted,
  children,
}: {
  id: T;
  active: T;
  mounted: boolean;
  children: ReactNode;
}) {
  if (!mounted) return null;
  return (
    <section role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} hidden={id !== active} className="workspace-panel">
      {children}
    </section>
  );
}

/**
 * Whether the screen is phone-width, for the few things that move rather than reflow -- the bottom
 * action bar replaces the header's buttons. Everything else is CSS.
 *
 * False wherever `matchMedia` doesn't exist (the test environment), which is the desktop layout.
 */
export function useIsNarrow(query = "(max-width: 767px)"): boolean {
  const [narrow, setNarrow] = useState(() => typeof window.matchMedia === "function" && window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia(query);
    const onChange = () => setNarrow(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [query]);
  return narrow;
}
